/**
 * Pure helpers for the contract-templates admin page. Extracted so the
 * publish/offer rules can be unit-tested without rendering, and to keep the
 * page component simple.
 */
import type {
  ContractTemplate,
  ContractTemplateVersion,
} from '../../services/contractTemplates.service';

export type VersionStatus = 'live' | 'superseded' | 'draft';

/** A published version is `live` when it is the highest published one,
 *  otherwise `superseded` (kept as an immutable record). Unpublished → draft. */
export function versionStatus(
  v: ContractTemplateVersion,
  liveVersion: number | null,
): VersionStatus {
  if (!v.is_published) return 'draft';
  return liveVersion !== null && v.version === liveVersion ? 'live' : 'superseded';
}

/** Forward-only WORM rule: a draft can be published only if it is strictly
 *  newer than the current live version. */
export function isPublishable(
  v: ContractTemplateVersion,
  liveVersion: number | null,
): boolean {
  return !v.is_published && (liveVersion === null || v.version > liveVersion);
}

/** Short status for the template header badge. */
export function offerStateLabel(t: ContractTemplate): string {
  if (t.is_offerable) return `Live · v${t.live_version}`;
  if (!t.is_active) return 'Inactive';
  return 'No published version';
}

export function scopeLabel(t: ContractTemplate): string {
  if (t.scope === 'product') return `Product · ${t.product_name ?? '—'}`;
  return 'General (all clients)';
}

export function formatBytes(n: number | null): string {
  if (n === null || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Products that have no product-scoped template yet: the ones a new template can be made for. */
export function productsWithoutTemplate<P extends { product_id: string }>(
  products: readonly P[],
  templates: readonly ContractTemplate[],
): P[] {
  const covered = new Set(
    templates.filter((t) => t.scope === 'product' && t.product_id).map((t) => t.product_id),
  );
  return products.filter((p) => !covered.has(p.product_id));
}

/** What is wrong with an upload, in plain words, or null when it can be sent. */
export function uploadProblem(input: { file: File | null; reason: string }): string | null {
  if (input.reason.trim().length < 4) return 'Give a reason of at least 4 characters.';
  if (input.file && !input.file.name.toLowerCase().endsWith('.pdf')) {
    return 'The file must be a PDF.';
  }
  return null;
}
